import React, { useState, useEffect } from 'react'
import { Helmet } from "react-helmet";
import { Link } from 'react-router-dom'
import Navbar from './components/Navbar'
import Footer from './components/Footer'

const CATEGORIES = [
  { label: 'All', value: '' },
  { label: 'UK Weddings', value: 'wedding' },
  { label: 'Commercial & Branding', value: 'commercial' },
  { label: 'Documentary Projects', value: 'documentary' },
  { label: 'Portraiture', value: 'portrait' },
  { label: 'Creative Process & Tips', value: 'tips' },
]

const MainBlog = () => {
  const [posts, setPosts] = useState([])
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState('')
  const [subForm, setSubForm] = useState({ fullName: '', email: '', interest: '' })
  const [subStatus, setSubStatus] = useState(null)

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/post`)
      .then(res => res.json())
      .then(data => setPosts(data))
  }, [])

  const filtered = posts.filter(post => {
    const matchesSearch =
      search === '' ||
      post.name?.toLowerCase().includes(search.toLowerCase()) ||
      post.category?.toLowerCase().includes(search.toLowerCase())
    const matchesCategory =
      activeCategory === '' ||
      post.category?.toLowerCase().includes(activeCategory.toLowerCase())
    return matchesSearch && matchesCategory
  })

  const handleSubChange = (e) =>
    setSubForm(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const handleSubSubmit = async (e) => {
    e.preventDefault()
    setSubStatus('loading')
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subForm),
      })
      const data = await res.json()
      if (data.success) {
        setSubStatus('success')
        setSubForm({ fullName: '', email: '', interest: '' })
        e.target.reset()
      } else {
        setSubStatus('error')
      }
    } catch {
      setSubStatus('error')
    }
  }

  return (
    <>
      <Helmet>
        <title>The Glintz Journal | UK Photography Insights & Guides</title>
        <meta
          name="description"
          content="Explore expert UK photography guides, behind-the-scenes visual essays, and practical resources for planning your commercial campaigns or luxury weddings."
        />
      </Helmet>

      <Navbar />

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="relative bg-black overflow-hidden flex items-end min-h-[60vh]">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black opacity-90" />

        <div className="relative z-10 w-full xl:px-44 lg:px-24 px-6 pb-16 xl:pb-24 pt-40">
          <p
            className="text-white/40 uppercase tracking-[.4em] text-xs mb-5"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            The Glintz Journal
          </p>
          <h1 className="text-white uppercase xl:text-6xl lg:text-5xl md:text-4xl text-3xl leading-[1.05] tracking-[.08em] mb-6 max-w-3xl">
            UK Photography Insights &amp; Stories
          </h1>
          <p
            className="text-white/60 text-sm md:text-base max-w-xl leading-relaxed"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            Expert guides, behind-the-scenes visual essays, and practical resources
            for planning your next commercial campaign or private celebration.
          </p>
        </div>
      </section>

      {/* ── DISCOVERY BAR ────────────────────────────────────────────────────── */}
      <section className="bg-white border-b border-gray-100 xl:px-44 lg:px-24 px-6 py-12">
        {/* Search */}
        <div className="relative max-w-xl mb-10">
          <input
            type="text"
            placeholder="Search the Journal..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full border border-gray-200 px-5 py-4 text-sm focus:outline-none focus:border-black transition pr-12"
            style={{ fontFamily: 'Muli, sans-serif' }}
          />
          <svg
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </div>

        {/* Category filters */}
        <div>
          <p
            className="text-gray-400 uppercase tracking-[.3em] text-xs mb-4"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            Explore by Category
          </p>
          <div className="flex flex-wrap gap-3">
            {CATEGORIES.map(cat => (
              <button
                key={cat.value}
                onClick={() => setActiveCategory(cat.value)}
                className={`px-5 py-2 text-xs uppercase tracking-[.2em] border transition-all ${
                  activeCategory === cat.value
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-black'
                }`}
                style={{ fontFamily: 'Muli, sans-serif' }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── ARTICLE GRID ─────────────────────────────────────────────────────── */}
      <section className="bg-white xl:px-44 lg:px-24 px-6 py-20">
        {filtered.length === 0 ? (
          <p
            className="text-gray-400 text-sm uppercase tracking-widest text-center py-20"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            No articles found.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
            {filtered.map(post => (
              <article key={post._id || post.slug} className="group flex flex-col">
                <Link to={`/blog/${post.slug}`} className="overflow-hidden block mb-5">
                  {post.coverImage && (
                    <img
                      src={post.coverImage}
                      alt={post.name}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                </Link>

                {post.category && (
                  <p
                    className="text-gray-400 uppercase tracking-[.3em] text-[10px] mb-2"
                    style={{ fontFamily: 'Muli, sans-serif' }}
                  >
                    {post.category}
                  </p>
                )}

                <Link to={`/blog/${post.slug}`}>
                  <h2 className="uppercase tracking-[.1em] text-gray-900 text-base font-semibold leading-snug mb-3 group-hover:opacity-70 transition-opacity">
                    {post.name}
                  </h2>
                </Link>

                <Link
                  to={`/blog/${post.slug}`}
                  className="mt-auto text-xs uppercase tracking-[.25em] text-gray-400 hover:text-black transition-colors flex items-center gap-2"
                  style={{ fontFamily: 'Muli, sans-serif' }}
                >
                  Read Article <span className="transition-transform group-hover:translate-x-1">→</span>
                </Link>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* ── SEO BLOCK ────────────────────────────────────────────────────────── */}
      <section className="bg-gray-50 border-t border-gray-100 xl:px-44 lg:px-24 px-6 py-24">
        <p
          className="text-gray-400 uppercase tracking-[.4em] text-xs mb-6"
          style={{ fontFamily: 'Muli, sans-serif' }}
        >
          Expert Resources
        </p>
        <h2 className="text-gray-900 uppercase xl:text-4xl lg:text-3xl text-2xl tracking-[.08em] leading-tight mb-8 max-w-2xl">
          Expert UK Photography Resources &amp; Guides
        </h2>
        <div className="grid xl:grid-cols-2 gap-12">
          <p
            className="text-gray-500 text-sm md:text-base leading-relaxed"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            We share over a decade of industry expertise, offering practical advice on everything
            from selecting the right visual style to understanding the nuances of commercial image
            licensing. Our goal is to equip you with the knowledge necessary to execute a flawless,
            stress-free production.
          </p>
          <p
            className="text-gray-500 text-sm md:text-base leading-relaxed"
            style={{ fontFamily: 'Muli, sans-serif' }}
          >
            Whether you are a brand manager planning a nationwide campaign or a couple navigating
            the world of luxury wedding photography in the UK, The Glintz Journal is your
            definitive resource.
          </p>
        </div>
      </section>

      {/* ── NEWSLETTER ───────────────────────────────────────────────────────── */}
      <section className="bg-black xl:px-44 lg:px-24 px-6 py-24 xl:py-32">
        <div className="grid xl:grid-cols-2 gap-16 items-start">
          <div>
            <p
              className="text-white/40 uppercase tracking-[.4em] text-xs mb-6"
              style={{ fontFamily: 'Muli, sans-serif' }}
            >
              Stay Connected
            </p>
            <h2 className="text-white uppercase xl:text-5xl lg:text-4xl text-3xl tracking-[.08em] leading-tight mb-6">
              Elevate Your Feed
            </h2>
            <p
              className="text-white/60 text-sm leading-relaxed"
              style={{ fontFamily: 'Muli, sans-serif' }}
            >
              Join the Glintz Media mailing list to receive our latest visual stories, UK venue
              guides, and exclusive commercial branding tips directly in your inbox.
            </p>
          </div>

          <form onSubmit={handleSubSubmit} className="space-y-5">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="subFullName"
                className="text-white/40 text-xs uppercase tracking-widest"
                style={{ fontFamily: 'Muli, sans-serif' }}
              >
                Full Name
              </label>
              <input
                id="subFullName"
                name="fullName"
                type="text"
                value={subForm.fullName}
                onChange={handleSubChange}
                required
                className="border border-white/20 bg-transparent text-white px-4 py-3 text-sm focus:outline-none focus:border-white transition"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="subEmail"
                className="text-white/40 text-xs uppercase tracking-widest"
                style={{ fontFamily: 'Muli, sans-serif' }}
              >
                Email Address
              </label>
              <input
                id="subEmail"
                name="email"
                type="email"
                value={subForm.email}
                onChange={handleSubChange}
                required
                className="border border-white/20 bg-transparent text-white px-4 py-3 text-sm focus:outline-none focus:border-white transition"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="interest"
                className="text-white/40 text-xs uppercase tracking-widest"
                style={{ fontFamily: 'Muli, sans-serif' }}
              >
                Area of Interest
              </label>
              <select
                id="interest"
                name="interest"
                value={subForm.interest}
                onChange={handleSubChange}
                required
                className="border border-white/20 bg-black text-white px-4 py-3 text-sm focus:outline-none focus:border-white transition"
              >
                <option value="">Select…</option>
                <option value="Personal">Personal</option>
                <option value="Corporate">Corporate</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={subStatus === 'loading'}
              className="mt-2 bg-white text-black px-10 py-3 text-xs tracking-[.25em] uppercase hover:bg-white/90 transition-all disabled:opacity-50"
              style={{ fontFamily: 'Muli, sans-serif' }}
            >
              {subStatus === 'loading' ? 'Subscribing…' : 'Subscribe'}
            </button>

            {subStatus === 'success' && (
              <p className="text-sm text-green-400 uppercase tracking-widest" style={{ fontFamily: 'Muli, sans-serif' }}>
                You're on the list — welcome to the Journal.
              </p>
            )}
            {subStatus === 'error' && (
              <p className="text-sm text-red-400 uppercase tracking-widest" style={{ fontFamily: 'Muli, sans-serif' }}>
                Something went wrong. Please try again.
              </p>
            )}
          </form>
        </div>
      </section>

      <Footer />
    </>
  )
}

export default MainBlog
